/**
 * ComposingReadout — the Copilot's signature instrument readout, rendered as an HTML overlay over the
 * assistant turn's chart slot (never inside the ECharts canvas; the ECharts native title stays off and
 * this caption is HTML). It carries two beats:
 *
 *  1. Wait (streaming, no answer yet): a NEUTRAL composing slot — reserved height, no data-like marks
 *     (the chart kind is unknown until `result`, so a bar/axis skeleton would mislead) — plus a quiet
 *     "Composing…" line. Shown for ALL streaming turns.
 *  2. Readout → caption (a chart result that carries an in-range count): the count reads UP first
 *     ("Composing from N records…"), decelerating to its true value, then LOCKS — the digits simply stop
 *     and settle dim→full-ink (no pulse, no glow, no tick, no scale at the lock instant) — then DEMOTES
 *     (a separate beat: scale + opacity, ≈0 translation) to the settled caption "N records in range"
 *     while the chart mounts into the slot beneath it.
 *
 * No-count path: there is nothing to render here (the chart builds with its normal entrance and no
 * records caption). Reduced motion: the settled caption appears at once (CountUp snaps; no demote tween).
 */
import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { tokens } from './tokens';
import { CountUp } from './charts/CountUp';
import { COUNT_READUP_SEC } from './motion';
import { TIMING } from './animationConfig';

// A cardinal highlight sweeps across the word while the model works — a calm "alive, thinking" cue (not a
// spinner). Pure CSS gradient-on-text; gated off under reduced motion (a static label is shown instead).
const shimmer = keyframes`
  0% { background-position: 200% center; }
  100% { background-position: -200% center; }
`;
const Shimmer = styled.span`
  background: linear-gradient(110deg, ${tokens.text.secondary} 30%, ${tokens.brand.cardinal} 50%, ${tokens.text.secondary} 70%);
  background-size: 200% auto;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  font-weight: 600;
  animation: ${shimmer} ${TIMING.shimmerSweepSec}s linear infinite;
`;

/** Seconds since the wait began — a quiet "still working" signal under the shimmer. Pauses under reduced motion. */
function useElapsedSeconds(active: boolean): number {
  const [s, setS] = React.useState(0);
  React.useEffect(() => {
    if (!active) return;
    const t = setInterval(() => setS((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, [active]);
  return s;
}

/**
 * A small, inline shimmering working line ("Composing…", "Querying the database…") + optional elapsed
 * timer. The SAME indicator carries across the whole pre-chart flow (compose → query) so the phases read
 * as one continuous "working" state, not a shimmer that vanishes before a separate panel appears.
 */
export function WorkingLine({ label, showTimer = false, testid }: { label: string; showTimer?: boolean; testid?: string }) {
  const reduceMotion = useReducedMotion() ?? false;
  const secs = useElapsedSeconds(showTimer && !reduceMotion);
  return (
    <div
      data-testid={testid}
      style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, fontFamily: tokens.fontFamily, fontSize: 14 }}
    >
      {reduceMotion ? <span style={{ color: tokens.text.secondary, fontWeight: 600 }}>{label}</span> : <Shimmer>{label}</Shimmer>}
      {showTimer && !reduceMotion && (
        // Decorative — the live region announces "working" once; the per-second tick must not be read out.
        <span aria-hidden="true" style={{ fontSize: 12, color: tokens.text.muted, fontVariantNumeric: 'tabular-nums' }}>
          {secs}s
        </span>
      )}
    </div>
  );
}

/**
 * The streaming-wait indicator: a shimmering stage label (e.g. "Reading your question…" →
 * "Reasoning over the cohort's data…") + elapsed timer, kept mounted the whole wait. The label reflects
 * the REAL pipeline stage (driven by whether the model has started streaming its reasoning); the streamed
 * reasoning prose above is the actual thought content being revealed.
 */
export function ComposingSlot({ label = 'Composing…' }: { label?: string }) {
  return <WorkingLine label={label} showTimer testid="composing-slot" />;
}

/**
 * The record readout/caption for a chart turn that carries a count. While `mounting` is true the readout
 * is large and reads up ("Composing from N records…"); once the chart has mounted it has demoted to the
 * small settled caption ("N records in range"). The demote is a scale+opacity beat (≈0 travel) distinct
 * from the count incrementing — it overlaps the chart draw.
 */
export function RecordReadout({ count, mounting }: { count: number; mounting: boolean }) {
  const reduceMotion = useReducedMotion() ?? false;
  const formatted = count.toLocaleString();

  if (mounting && !reduceMotion) {
    // Lead beat: the count reads up large. CountUp owns the dim→full-ink lock and keeps the rolling
    // digits aria-hidden, exposing only the final value to AT (once).
    return (
      <div
        data-testid="record-readout"
        style={{
          fontFamily: tokens.fontFamily,
          fontSize: 28,
          fontWeight: 700,
          lineHeight: 1.2,
          color: tokens.text.primary,
        }}
      >
        Querying <CountUp value={count} duration={COUNT_READUP_SEC} /> records…
      </div>
    );
  }

  // Settled caption: the demote lands here (scale+opacity in, ≈0 translation). Under reduced motion it
  // simply appears. The caption reads the true in-range count — not the number of plotted marks.
  return (
    <motion.div
      data-testid="records-caption"
      // Opacity-only demote: a scale here fought the chart's simultaneous y:12→0 rise (two transforms in
      // the same spot, same 0.4s) — the caption just fades to its settled size while the chart rises.
      initial={reduceMotion ? false : { opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      style={{
        fontFamily: tokens.fontFamily,
        fontSize: 13,
        fontWeight: 600,
        color: tokens.text.secondary,
        marginBottom: 8,
      }}
    >
      {formatted} records in range
    </motion.div>
  );
}
